<template>
  <section class="product-safety-section" v-if="!isLoading">
    <ProductSafetyHeader
      title="Ürün Güvenliği Bilgileri"
      description="Ürün güvenliğine ilişkin ilgili mevzuat kapsamında tarafınızca sağlanması gereken, ürün görsel ve kılavuzlarını ilgili alanlardan yükleyebilir; ürüne ait üretici, ithalatçı/iktisadi işletmeci, ek bilgileri (materyal bileşeni, bakım talimatı, CE uygunluk sembolü, yıkama talimatı, ürün uyarı sembolleri, ürün güvenliği uyarıları, içerik listesi, tamir edilebilirlik) ve varsa besin değer ve bilgilerini ilgili alanlara girebilirsiniz."
    />

    <transition name="fade">
      <div class="section-content">
        <div class="top-row">
          <ProductSafetyVisuals
            ref="productSafetyVisualsRef"
            :safetyImages="safetyImages"
            :safetyDocuments="safetyDocuments"
            :onSaveChanges="submitAllSafetyInfo"
            v-model:applyToVariants="changesAll"
            :isProductDisabled="isProductDisabled"
            :isSavingAllChanges="isSavingAllChanges"
            @hasChanges="$emit('hasChanges')"
          />
        </div>

        <div class="info-row">
          <ManufacturerInfo
            ref="manufacturerInfoRef"
            :onSaveChanges="submitAllSafetyInfo"
            :safetyInfoData="safetyInfoData"
            :isSavingAllChanges="isSavingAllChanges"
            v-model:applyToVariants="changesAll"
            :isProductDisabled="isProductDisabled"
            @hasChanges="$emit('hasChanges')"
          />
        </div>

        <ExtraInfo
          ref="extraInfoRef"
          :onSaveChanges="submitAllSafetyInfo"
          :safetyInfoData="safetyInfoData"
          v-model:applyToVariants="changesAll"
          :isProductDisabled="isProductDisabled"
          :isSavingAllChanges="isSavingAllChanges"
          @hasChanges="$emit('hasChanges')"
        />
      </div>
    </transition>
  </section>

  <ProductSafetyInfosSkeleton v-else />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import ProductSafetyHeader from './ProductSafetyComponent/partial/ProductSafetyHeader.vue'
import ProductSafetyVisuals from './ProductSafetyComponent/partial/ProductSafetyVisuals.vue'
import ManufacturerInfo from './ProductSafetyComponent/partial/ManufacturerInfo.vue'
import ExtraInfo from './ProductSafetyComponent/partial/ExtraInfo.vue'
import ProductSafetyInfosSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductSafetyInfosSkeleton.vue'

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  isSavingAllChanges: {
    type: Boolean,
    default: false,
  },
  getNutritionInfo: {
    type: Function,
    default: null,
  },
  productId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['hasChanges', 'saved'])

const store = useStore()

const isProductDisabled = computed(() => store.getters['products/isProductDisabled'])

const manufacturerInfoRef = ref(null)
const productSafetyVisualsRef = ref(null)
const extraInfoRef = ref(null)
const changesAll = ref(true)

const safetyInfoData = computed(() => store.getters['products/getProductSafetyInfo'])
const safetyImages = computed(() => safetyInfoData.value?.images || [])
const safetyDocuments = computed(() => safetyInfoData.value?.documents || [])

// Üretici bilgilerini alır
const getManufacturerInfoData = () => {
  return manufacturerInfoRef.value?.getManufacturerInfo()
}

// Ek bilgileri alır
const getAdditionalInfoData = () => {
  return extraInfoRef.value?.getData() || {}
}

// Görsel ve döküman verilerini alır
const getVisualsData = () => {
  return {
    selectedImages: productSafetyVisualsRef.value?.selectedImages || [],
    selectedDocuments: productSafetyVisualsRef.value?.selectedDocuments || [],
    uploadedImages: productSafetyVisualsRef.value?.uploadedImages || [],
    uploadedDocuments: productSafetyVisualsRef.value?.uploadedDocuments || []
  }
}

// Seçili ve yüklenen dosyalardan payload oluşturur
const buildFilePayload = (selected, uploaded, type, currentProductId) => {
  const processed = new Set()
  const result = []

  const existingInSelected = selected.filter(item =>
    item &&
    !item.file &&
    (item.tempId || item.id || item.url || (type === 'image' ? item.image : null))
  )

  existingInSelected.forEach(item => {
    const tempId = item.tempId || item.id
    if (tempId && !processed.has(tempId)) {
      const isInUploaded = uploaded.some(u => (u.tempId === tempId || u.id === tempId))

      if (isInUploaded) {
        result.push({
          tempId: tempId,
          order: result.length,
          originalName: item.fileName || item.title || item.originalName || type,
          filePath: item.url || (type === 'image' ? item.image : null) || item.filePath || ''
        })
      } else {
        result.push({
          id: tempId,
          productId: currentProductId,
          order: result.length,
          originalName: item.fileName || item.title || item.originalName || type,
          filePath: item.url || (type === 'image' ? item.image : null) || item.filePath || ''
        })
      }
      processed.add(tempId)
    }
  })

  uploaded.forEach(item => {
    const tempId = item.tempId || item.id
    if (tempId && !processed.has(tempId)) {
      result.push({
        tempId: tempId,
        order: result.length,
        originalName: item.fileName || item.originalName || type,
        filePath: item.url || item.filePath || ''
      })
      processed.add(tempId)
    }
  })

  return result
}

// Görseller ve dökümanlar için payload oluşturur
const buildImagesAndDocumentsPayload = (visualsData, currentProductId) => {
  const imagesPayload = buildFilePayload(
    visualsData.selectedImages,
    visualsData.uploadedImages,
    'image',
    currentProductId
  )

  const documentsPayload = buildFilePayload(
    visualsData.selectedDocuments,
    visualsData.uploadedDocuments,
    'document',
    currentProductId
  )

  return { imagesPayload, documentsPayload }
}

const buildSafetyInfoPayload = (manufacturerInfo, additionalInfo, imagesPayload, documentsPayload, applyToAll, nutritionInfo) => {
  return {
    changesAll: applyToAll,
    productSafetyRequest: {
      providers: manufacturerInfo?.data || [],
      images: imagesPayload,
      documents: documentsPayload,
      additionalInfo: additionalInfo,
      ...(nutritionInfo ? { nutritionInfo } : {})
    }
  }
}

const submitAllSafetyInfo = async (nutritionInfo = null) => {
  try {
    const manufacturerInfo = getManufacturerInfoData()
    const additionalInfo = getAdditionalInfoData()
    const visualsData = getVisualsData()

    let finalNutritionInfo = nutritionInfo
    if (!finalNutritionInfo && props.getNutritionInfo) {
      finalNutritionInfo = props.getNutritionInfo()
    }

    const { imagesPayload, documentsPayload } = buildImagesAndDocumentsPayload(
      visualsData,
      props.productId
    )

    const payload = buildSafetyInfoPayload(
      manufacturerInfo,
      additionalInfo,
      imagesPayload,
      documentsPayload,
      changesAll.value,
      finalNutritionInfo
    )

    const response = await store.dispatch('products/safetyInfoSubmit', {
      productId: props.productId,
      payload
    })

    emit('saved')
    return response
  } catch (error) {
    console.error('Error saving safety info:', error)
    throw error
  }
}

const showSaveAllSuccessState = () => { 
  if (!productSafetyVisualsRef.value?.combinedIsSaved) {
    productSafetyVisualsRef.value?.showSaveAllSuccessState?.()
  }
  if (!manufacturerInfoRef.value?.combinedIsSaved) {
    manufacturerInfoRef.value?.showSaveAllSuccessState?.()
  }
  
  if (!extraInfoRef.value?.combinedIsSaved) {
    extraInfoRef.value?.showSaveAllSuccessState?.()
  }
}

const getAllSafetyInfoData = (nutritionInfo = null) => {
  try {
    const manufacturerInfo = getManufacturerInfoData()
    const additionalInfo = getAdditionalInfoData()
    const visualsData = getVisualsData()
    
    let finalNutritionInfo = nutritionInfo
    if (!finalNutritionInfo && props.getNutritionInfo) {
      finalNutritionInfo = props.getNutritionInfo()
    }
    
    const { imagesPayload, documentsPayload } = buildImagesAndDocumentsPayload(
      visualsData,
      props.productId
    )
    
    const payload = buildSafetyInfoPayload(
      manufacturerInfo,
      additionalInfo,
      imagesPayload,
      documentsPayload,
      changesAll.value,
      finalNutritionInfo
    )
    
    return payload
  } catch (error) {
    console.error('Error collecting safety info:', error)
    return null
  }
}

defineExpose({
  submitAllSafetyInfo,
  getAllSafetyInfoData,
  showSaveAllSuccessState
})
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.product-safety-section {
  border: 1px solid $gray-19;
  border-radius: 8px;
  background: $colorN00;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.section-content {
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.top-row,
.info-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0;
}
</style>
